/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { CloseIcon } from '../assets/svg/MenuIcons';
import {
  formatAmount,
  webFormatCurrency,
  installMentPlans,
} from '@rb-serve-app/core';
import moment from 'moment';
import { useSelector } from 'react-redux';

interface EqualPaymentPlanModalProps {
  plan?: installMentPlans;
  handleCloseModal: () => void;
}

const EqualPaymentPlanModal: React.FC<EqualPaymentPlanModalProps> = ({
  plan,
  handleCloseModal,
}) => {
  let bgColor;
  if (plan?.planStatus === 'ACTIVE') {
    bgColor = 'bg-primary-red';
  } else if (plan?.planStatus === 'CLOSED') {
    bgColor = 'bg-progressbar-success';
  } else {
    bgColor = 'bg-progressbar-deactive';
  }

  const cardStyle = `flex flex-col items-center p-6 mt-[19px] mb-5 rounded-lg text-center text-white ${bgColor}`;

  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );

  const { entries } = contentfulData;
  const contentData = entries?.rb_view_epp;
  const lang = localStorage.getItem('language') || 'en-US';

  function getStatusTitle(status: string | undefined): string {
    if (status === 'ACTIVE') return contentData.activeStatusTitle;
    if (status === 'CLOSED') return contentData.completedStatusTitle;
    return contentData.cancelledStatusTitle;
  }

  return (
    <div className="p-6">
      {plan && contentData && (
        <>
          <div className="flex justify-between flex-row items-center">
            <h2
              className="text-xl font-bold text-black"
              aria-label={contentData.planDetailsTitle}
            >
              {contentData.planDetailsTitle}
            </h2>
            <button
              className="text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
              onClick={handleCloseModal}
              aria-label={contentData.closeModalLabel}
            >
              <span className="sr-only">Close</span>
              <CloseIcon />
            </button>
          </div>
          <div className="modal-content max-h-[85vh] overflow-y-auto pr-4">
            <div
              className={cardStyle}
              role="region"
              aria-labelledby="plan-title"
            >
              <div
                className={`bg-white text-xs rounded-full px-2 py-1 mb-4 ${
                  plan?.planStatus === 'ACTIVE'
                    ? 'text-primary-red'
                    : 'text-progressbar-deactive'
                } w-fit text-center`}
                aria-label={getStatusTitle(plan?.planStatus)}
              >
                <span className="block text-xs">
                  {getStatusTitle(plan?.planStatus)}
                </span>
              </div>
              <h2
                id="plan-title"
                className="text-lg font-bold"
                aria-label={plan?.planName}
              >
                {plan?.planName}
              </h2>

              {plan?.planStatus === 'ACTIVE' && (
                <h3
                  className="text-2xl font-bold mb-1"
                  aria-label={plan?.outstandingInstallmentAmount?.value}
                >
                  {`${webFormatCurrency(Number(plan?.outstandingInstallmentAmount?.value) || 0, lang)}` ||
                    '--.--'}
                </h3>
              )}
              {plan?.planStatus === 'ACTIVE' && (
                <p
                  className="text-xs font-light"
                  aria-label={`${contentData.nextPaymentDueOnTitle} ${plan?.nextPaymentDate}`}
                >
                  {contentData.nextPaymentDueOnTitle}{' '}
                  {moment(plan?.nextPaymentDate).format('DD MMM, YYYY')}
                </p>
              )}
            </div>

            <div className="p-3 md:p-6 border border-light-gray-border rounded-md">
              <div className="flex justify-between items-center">
                <p
                  className="text-sm text-gray-600 font-[500] ml-[2px] md:inline flex flex-col"
                  aria-label={`${contentData.paidTitle} ${plan?.principleCompleted}`}
                >
                  {contentData.paidTitle}{' '}
                  <span className="text-sm font-bold text-gray-800">
                    {`${webFormatCurrency(Number(plan?.principleCompleted) || 0, lang)}` ||
                      '--.--'}
                  </span>
                </p>
                <p
                  className="text-sm text-gray-600 font-[500]  md:inline flex flex-col items-end"
                  aria-label={`${contentData.remainingTitle} ${plan?.outstandingTransactionAmount?.value}`}
                >
                  {contentData.remainingTitle}{' '}
                  <span className="text-sm font-bold text-gray-800">
                    {`${webFormatCurrency(Number(plan?.outstandingTransactionAmount?.value) || 0, lang)}`}
                  </span>
                </p>
              </div>

              <div className="mt-2">
                <div
                  className="h-2 w-full bg-gray-300 rounded-full overflow-hidden"
                  role="progressbar"
                  aria-valuenow={plan?.completedPercentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className={`h-full ${
                      plan?.planStatus === 'CANCELLED'
                        ? 'bg-progressbar-deactive'
                        : 'bg-progressbar-success'
                    }`}
                    style={{ width: `${plan.completedPercentage}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-[2px] font-[500]">
                  {plan?.planStatus === 'CANCELLED'
                    ? contentData.eppStatusCancelledSubtitle
                    : contentData.eppStatusCompletedSubtitle.replace(
                        '%s',
                        `${plan.completedPercentage.toString()}%`
                      )}
                </p>
              </div>
            </div>

            <div className="text-sm text-gray-900">
              <div className="flex justify-between py-4 border-b border-light-gray-border">
                <span
                  className="text-gray-700"
                  aria-label={contentData.principalAmountTitle}
                >
                  {contentData.principalAmountTitle}
                </span>
                <span
                  className="font-medium"
                  aria-label={plan?.originalTransactionAmount?.value}
                >
                  {`${webFormatCurrency(Number(plan?.originalTransactionAmount?.value) || 0, lang)}` ||
                    '--.--'}
                </span>
              </div>
              <div className="flex justify-between py-4 border-b border-light-gray-border">
                <span
                  className="text-gray-700"
                  aria-label={contentData.aprTitle}
                >
                  {contentData.aprTitle}
                </span>
                <span className="font-medium" aria-label={plan?.apr}>
                  {plan?.apr}%
                </span>
              </div>
              <div className="flex justify-between py-4 border-b border-light-gray-border">
                <span
                  className="text-gray-700"
                  aria-label={contentData.totalNumberOfPaymentsTitle}
                >
                  {contentData.totalNumberOfPaymentsTitle}
                </span>
                <span className="font-medium">
                  {plan?.totalNumberOfInstallments}
                </span>
              </div>
              <div className="flex justify-between py-4 border-b border-light-gray-border mb-11">
                <span
                  className="text-gray-700"
                  aria-label={contentData.minimumMonthlyAmountTitle}
                >
                  {contentData.minimumMonthlyAmountTitle}
                </span>
                <span
                  className="font-medium"
                  aria-label={plan?.outstandingInstallmentAmount?.value}
                >
                  {`${webFormatCurrency(Number(plan?.outstandingInstallmentAmount?.value) || 0, lang)}` ||
                    '--.--'}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EqualPaymentPlanModal;
